import { useCallback, useEffect, useRef, useState } from 'react';

export interface FilterToastItem {
    id: number;
    summary: string;
    total: number;
}

const DEFAULT_DURATION = 6000;
/**
 * Toasts stack so a user changing several filters quickly can see that each
 * change took. Past a handful they stop being reassuring and start covering the
 * page, so the oldest is dropped once we exceed this.
 */
const DEFAULT_MAX_VISIBLE = 4;

interface TimerRecord {
    timer: ReturnType<typeof setTimeout> | null;
    /** Wall-clock expiry, so a pause can resume with the time that was left. */
    expiresAt: number;
    remaining: number;
}

export function useFilterToast(
    duration = DEFAULT_DURATION,
    maxVisible = DEFAULT_MAX_VISIBLE
) {
    const [toasts, setToasts] = useState<FilterToastItem[]>([]);
    const timersRef = useRef(new Map<number, TimerRecord>());
    const idRef = useRef(0);

    const clearTimer = useCallback((id: number) => {
        const record = timersRef.current.get(id);
        if (record?.timer) {
            clearTimeout(record.timer);
            record.timer = null;
        }
    }, []);

    const remove = useCallback((id: number) => {
        clearTimer(id);
        timersRef.current.delete(id);
        setToasts((current) => current.filter((t) => t.id !== id));
    }, [clearTimer]);

    const arm = useCallback((id: number, ms: number) => {
        const record = timersRef.current.get(id);
        if (!record) {
            return;
        }
        record.expiresAt = Date.now() + ms;
        record.remaining = ms;
        record.timer = setTimeout(() => remove(id), ms);
    }, [remove]);

    const show = useCallback((summary: string, total: number) => {
        const id = ++idRef.current;
        setToasts((current) => {
            const next = [...current, { id, summary, total }];
            // Drop the oldest beyond the cap, and tear down their timers.
            while (next.length > maxVisible) {
                const evicted = next.shift();
                if (evicted) {
                    const record = timersRef.current.get(evicted.id);
                    if (record?.timer) {
                        clearTimeout(record.timer);
                    }
                    timersRef.current.delete(evicted.id);
                }
            }
            return next;
        });
        timersRef.current.set(id, { timer: null, expiresAt: 0, remaining: duration });
        arm(id, duration);
    }, [arm, duration, maxVisible]);

    // Hovering or focusing the stack holds every toast in place. Without this,
    // toasts would expire out from under a cursor heading for "Jump to graphs".
    const pause = useCallback(() => {
        timersRef.current.forEach((record, id) => {
            if (record.timer) {
                record.remaining = Math.max(0, record.expiresAt - Date.now());
                clearTimer(id);
            }
        });
    }, [clearTimer]);

    const resume = useCallback(() => {
        timersRef.current.forEach((record, id) => {
            if (!record.timer) {
                arm(id, record.remaining);
            }
        });
    }, [arm]);

    useEffect(() => {
        const timers = timersRef.current;
        return () => {
            timers.forEach((record) => {
                if (record.timer) {
                    clearTimeout(record.timer);
                }
            });
            timers.clear();
        };
    }, []);

    return { toasts, show, dismiss: remove, pause, resume };
}
